/** @format */

import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { doc, updateDoc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { toast } from "react-hot-toast";

import GeneralSettingsModal from "../components/GeneralSettingsModal";
import {
  ShieldCheckIcon,
  CheckIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  CloudArrowDownIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  ShareIcon,
  DevicePhoneMobileIcon,
  PlayIcon,
  ComputerDesktopIcon,
} from "@heroicons/react/24/outline";

const Settings = () => {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(false);

  const [showGeneralSettingsModal, setShowGeneralSettingsModal] =
    useState(false);

  // System settings (admin only)
  const [systemSettings, setSystemSettings] = useState({
    maintenance: false,
    registration: true,
    emailVerification: true,
    twoFactorAuth: false,
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    backupFrequency: "daily",
    logRetention: 90,
  });

  // Load maintenance status from Maintenance collection
  useEffect(() => {
    const loadMaintenanceStatus = async () => {
      if (!isAdmin) return;

      try {
        const maintenanceRef = doc(db, "Maintenance", "main");
        const maintenanceDoc = await getDoc(maintenanceRef);

        if (maintenanceDoc.exists()) {
          const maintenanceData = maintenanceDoc.data();
          setSystemSettings((prev) => ({
            ...prev,
            maintenance: maintenanceData.isEnabled || false,
          }));
        }
      } catch (error) {
        console.error("Error loading maintenance status:", error);
      }
    };

    loadMaintenanceStatus();
  }, [isAdmin]);

  const handleSystemSettingChange = (key, value) => {
    setSystemSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const saveSystemSettings = async () => {
    if (!isAdmin) return;

    setLoading(true);
    try {
      // Save to system settings collection (create if doesn't exist)
      const settingsRef = doc(db, "systemSettings", "main");
      await setDoc(
        settingsRef,
        {
          ...systemSettings,
          updatedAt: new Date(),
        },
        { merge: true }
      );

      // If maintenance mode is enabled, also save to Maintenance collection
      if (systemSettings.maintenance) {
        const maintenanceRef = doc(db, "Maintenance", "main");
        await setDoc(
          maintenanceRef,
          {
            isEnabled: true,
            enabledAt: new Date(),
            enabledBy: "admin", // You can replace this with actual user ID if needed
            reason: "Maintenance mode enabled via system settings",
            updatedAt: new Date(),
          },
          { merge: true }
        );
      } else {
        // If maintenance mode is disabled, update the Maintenance collection to false
        const maintenanceRef = doc(db, "Maintenance", "main");
        await setDoc(
          maintenanceRef,
          {
            isEnabled: false,
            disabledAt: new Date(),
            disabledBy: "admin", // You can replace this with actual user ID if needed
            reason: "Maintenance mode disabled via system settings",
            updatedAt: new Date(),
          },
          { merge: true }
        );
      }

      toast.success("System settings updated successfully!");
    } catch (error) {
      console.error("Error saving system settings:", error);
      toast.error("Failed to save system settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    ...(isAdmin
      ? [
          {
            id: "general",
            name: "General",
            icon: BuildingOfficeIcon,
            description: "Company information and client-side settings",
          },
          {
            id: "system",
            name: "System",
            icon: ShieldCheckIcon,
            description: "System configuration and security settings",
          },
        ]
      : []),
  ];

  const renderGeneralTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Company & Client Settings
        </h3>
        <p className="text-gray-600 mb-8">
          Current company information, contact details, social media links, and
          study app configurations that will be displayed on the client side.
        </p>

        {/* Company Information */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200 mb-6 shadow-sm">
          <div className="flex items-center mb-4">
            <GlobeAltIcon className="w-6 h-6 text-blue-600 mr-3" />
            <h4 className="font-semibold text-blue-900 text-xl">
              Company Information
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg border border-blue-100">
              <label className="block text-sm font-medium text-blue-700 mb-2 flex items-center">
                <BuildingOfficeIcon className="w-4 h-4 mr-2" />
                Company Name
              </label>
              <p className="text-blue-900 font-bold text-lg">
                Profs Training Solutions
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-blue-100">
              <label className="block text-sm font-medium text-blue-700 mb-2 flex items-center">
                <MapPinIcon className="w-4 h-4 mr-2" />
                Address
              </label>
              <p className="text-blue-900 font-semibold">
                Fefeti Drive, Spintex Road
              </p>
              <p className="text-blue-900">Tema, Greater Accra, Ghana</p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-6 rounded-xl border border-emerald-200 mb-6 shadow-sm">
          <div className="flex items-center mb-4">
            <PhoneIcon className="w-6 h-6 text-emerald-600 mr-3" />
            <h4 className="font-semibold text-emerald-900 text-xl">
              Contact Information
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            <div className="bg-white p-4 rounded-lg border border-emerald-100">
              <label className="block text-sm font-medium text-emerald-700 mb-2 flex items-center">
                <PhoneIcon className="w-4 h-4 mr-2" />
                Phone Numbers
              </label>
              <div className="space-y-2">
                <p className="text-emerald-900 font-semibold">
                  +233 243 44 39 34
                </p>
                <p className="text-emerald-900 font-semibold">
                  +233 244 990 773
                </p>
                <p className="text-emerald-900 font-semibold">
                  +233 246 752 130
                </p>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-emerald-100">
              <label className="block text-sm font-medium text-emerald-700 mb-2 flex items-center">
                <ClockIcon className="w-4 h-4 mr-2" />
                Working Hours
              </label>
              <p className="text-emerald-900 font-bold text-lg">
                Mon-Fri: 9am-5pm
              </p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-emerald-100">
            <label className="block text-sm font-medium text-emerald-700 mb-2 flex items-center">
              <EnvelopeIcon className="w-4 h-4 mr-2" />
              Email Addresses
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <p className="text-emerald-900 font-semibold">
                profsa35@gmail.com
              </p>
              <p className="text-emerald-900 font-semibold">
                profstrainingsolutions@gmail.com
              </p>
            </div>
          </div>
        </div>

        {/* Social Media Links */}
        <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-6 rounded-xl border border-violet-200 mb-6 shadow-sm">
          <div className="flex items-center mb-4">
            <ShareIcon className="w-6 h-6 text-violet-600 mr-3" />
            <h4 className="font-semibold text-violet-900 text-xl">
              Social Media Links
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg border border-violet-100 hover:shadow-md transition-shadow">
              <label className="block text-sm font-medium text-violet-700 mb-2">
                Instagram
              </label>
              <a
                href="http://instagram.com/profs_training_solutions/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-violet-900 hover:text-violet-700 font-semibold"
              >
                @profs_training_solutions
              </a>
            </div>
            <div className="bg-white p-4 rounded-lg border border-violet-100 hover:shadow-md transition-shadow">
              <label className="block text-sm font-medium text-violet-700 mb-2">
                TikTok
              </label>
              <a
                href="http://tiktok.com/@profstraining"
                target="_blank"
                rel="noopener noreferrer"
                className="text-violet-900 hover:text-violet-700 font-semibold"
              >
                @profstraining
              </a>
            </div>
            <div className="bg-white p-4 rounded-lg border border-violet-100 hover:shadow-md transition-shadow">
              <label className="block text-sm font-medium text-violet-700 mb-2">
                YouTube
              </label>
              <a
                href="http://youtube.com/@ProfsTrainingSolutions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-violet-900 hover:text-violet-700 font-semibold"
              >
                @ProfsTrainingSolutions
              </a>
            </div>
            <div className="bg-white p-4 rounded-lg border border-violet-100 hover:shadow-md transition-shadow">
              <label className="block text-sm font-medium text-violet-700 mb-2">
                LinkedIn
              </label>
              <a
                href="https://linkedin.com/in/profs-training-solutions/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-violet-900 hover:text-violet-700 font-semibold"
              >
                profs-training-solutions
              </a>
            </div>
            <div className="bg-white p-4 rounded-lg border border-violet-100 hover:shadow-md transition-shadow">
              <label className="block text-sm font-medium text-violet-700 mb-2">
                Facebook
              </label>
              <a
                href="http://facebook.com/ProfsTraining"
                target="_blank"
                rel="noopener noreferrer"
                className="text-violet-900 hover:text-violet-700 font-semibold"
              >
                ProfsTraining
              </a>
            </div>
            <div className="bg-white p-4 rounded-lg border border-violet-100 hover:shadow-md transition-shadow">
              <label className="block text-sm font-medium text-violet-700 mb-2">
                WhatsApp Channel
              </label>
              <a
                href="https://whatsapp.com/channel/0029VaZV6OeEAKWHHdSdiR1S"
                target="_blank"
                rel="noopener noreferrer"
                className="text-violet-900 hover:text-violet-700 font-semibold"
              >
                Profs Training Solutions
              </a>
            </div>
            <div className="bg-white p-4 rounded-lg border border-violet-100 hover:shadow-md transition-shadow">
              <label className="block text-sm font-medium text-violet-700 mb-2">
                Telegram
              </label>
              <a
                href="https://t.me/Profstrainingsolutions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-violet-900 hover:text-violet-700 font-semibold"
              >
                @Profstrainingsolutions
              </a>
            </div>
          </div>
        </div>

        {/* Study Apps */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-xl border border-amber-200 mb-6 shadow-sm">
          <div className="flex items-center mb-4">
            <DevicePhoneMobileIcon className="w-6 h-6 text-amber-600 mr-3" />
            <h4 className="font-semibold text-amber-900 text-xl">Study Apps</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg border border-amber-100 hover:shadow-md transition-shadow">
              <label className="block text-sm font-medium text-amber-700 mb-2 flex items-center">
                <PlayIcon className="w-4 h-4 mr-2" />
                Google Play Store
              </label>
              <a
                href="https://play.google.com/store/apps/details?id=profstudymate.appev"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-900 hover:text-amber-700 font-semibold"
              >
                ProfStudyMate App
              </a>
            </div>
            <div className="bg-white p-4 rounded-lg border border-amber-100 hover:shadow-md transition-shadow">
              <label className="block text-sm font-medium text-amber-700 mb-2 flex items-center">
                <DevicePhoneMobileIcon className="w-4 h-4 mr-2" />
                Apple App Store
              </label>
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-900 hover:text-amber-700 font-semibold"
              >
                ProfStudyMate iOS
              </a>
            </div>
            <div className="bg-white p-4 rounded-lg border border-amber-100 hover:shadow-md transition-shadow">
              <label className="block text-sm font-medium text-amber-700 mb-2 flex items-center">
                <ComputerDesktopIcon className="w-4 h-4 mr-2" />
                Microsoft Store
              </label>
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-900 hover:text-amber-700 font-semibold"
              >
                ProfStudyMate Windows
              </a>
            </div>
          </div>
          <div className="mt-4 bg-white p-4 rounded-lg border border-amber-100">
            <label className="block text-sm font-medium text-amber-700 mb-2 flex items-center">
              <GlobeAltIcon className="w-4 h-4 mr-2" />
              Web Application
            </label>
            <a
              href="https://profstudymate.com/#"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-900 hover:text-amber-700 font-semibold text-lg"
            >
              profstudymate.com
            </a>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={() => setShowGeneralSettingsModal(true)}
            className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:scale-105 transition-all duration-200"
          >
            <BuildingOfficeIcon className="w-5 h-5 mr-2" />
            Edit General Settings
          </button>
        </div>
      </div>
    </div>
  );

  const renderSystemTab = () => (
    <div className="space-y-6">
      {/* System Configuration */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          System Configuration
        </h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              User Registration
            </label>
            <button
              onClick={() =>
                handleSystemSettingChange(
                  "registration",
                  !systemSettings.registration
                )
              }
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                systemSettings.registration ? "bg-green-600" : "bg-gray-200"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  systemSettings.registration
                    ? "translate-x-5"
                    : "translate-x-0"
                }`}
              />
            </button>
            <p className="text-xs text-gray-500 mt-1">
              Allow new user registrations
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Verification
            </label>
            <button
              onClick={() =>
                handleSystemSettingChange(
                  "emailVerification",
                  !systemSettings.emailVerification
                )
              }
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                systemSettings.emailVerification
                  ? "bg-green-600"
                  : "bg-gray-200"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  systemSettings.emailVerification
                    ? "translate-x-5"
                    : "translate-x-0"
                }`}
              />
            </button>
            <p className="text-xs text-gray-500 mt-1">
              Require email verification
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Session Timeout (minutes)
            </label>
            <input
              type="number"
              value={systemSettings.sessionTimeout}
              onChange={(e) =>
                handleSystemSettingChange(
                  "sessionTimeout",
                  parseInt(e.target.value)
                )
              }
              min="5"
              max="480"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Login Attempts
            </label>
            <input
              type="number"
              value={systemSettings.maxLoginAttempts}
              onChange={(e) =>
                handleSystemSettingChange(
                  "maxLoginAttempts",
                  parseInt(e.target.value)
                )
              }
              min="3"
              max="10"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Maintenance Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Maintenance</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maintenance Mode
            </label>
            <button
              onClick={() =>
                handleSystemSettingChange(
                  "maintenance",
                  !systemSettings.maintenance
                )
              }
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                systemSettings.maintenance ? "bg-red-600" : "bg-gray-200"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  systemSettings.maintenance ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
            <p className="text-xs text-gray-500 mt-1">
              Enable maintenance mode
            </p>
            {systemSettings.maintenance && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                <p className="text-xs text-red-700">
                  ⚠️ System is in maintenance mode. Users will see maintenance
                  page.
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Two-Factor Authentication
            </label>
            <button
              onClick={() =>
                handleSystemSettingChange(
                  "twoFactorAuth",
                  !systemSettings.twoFactorAuth
                )
              }
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                systemSettings.twoFactorAuth ? "bg-green-600" : "bg-gray-200"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  systemSettings.twoFactorAuth
                    ? "translate-x-5"
                    : "translate-x-0"
                }`}
              />
            </button>
            <p className="text-xs text-gray-500 mt-1">
              Require 2FA for admin accounts
            </p>
          </div>
        </div>
      </div>

      {/* Backup & Logs */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Backup & Logs
        </h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Backup Frequency
            </label>
            <select
              value={systemSettings.backupFrequency}
              onChange={(e) =>
                handleSystemSettingChange("backupFrequency", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Log Retention (days)
            </label>
            <input
              type="number"
              value={systemSettings.logRetention}
              onChange={(e) =>
                handleSystemSettingChange(
                  "logRetention",
                  parseInt(e.target.value)
                )
              }
              min="7"
              max="365"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="mt-6 flex space-x-3">
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <CloudArrowUpIcon className="w-4 h-4 mr-2" />
            Create Backup
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <CloudArrowDownIcon className="w-4 h-4 mr-2" />
            Restore Backup
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <ArrowPathIcon className="w-4 h-4 mr-2" />
            Clear Logs
          </button>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={saveSystemSettings}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <CheckIcon className="w-4 h-4 mr-2" />
          {loading ? "Saving..." : "Save System Settings"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="mt-2 text-gray-600">
            Manage company settings and system configuration
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center">
                    <tab.icon className="w-5 h-5 mr-2" />
                    {tab.name}
                  </div>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === "general" && renderGeneralTab()}
            {activeTab === "system" && renderSystemTab()}
          </div>
        </div>
      </div>

      {/* General Settings Modal */}
      <GeneralSettingsModal
        isOpen={showGeneralSettingsModal}
        onClose={() => setShowGeneralSettingsModal(false)}
      />
    </div>
  );
};

export default Settings;
